/**
 * @fileoverview Medical Staff Service
 * @description This file contains functions to interact with the medical staff API.
 * @author hectorCodes
 * @version 1.0
 * @date 2025-05-05
 */

import { BASE_URL } from "../constants";
import { getAuthHeader } from "../utils";

/**
 * @typedef MedicalStaff
 * @property {number} id
 * @property {string} name
 * @property {number[]} specialties
 * @property {string} is_assigned
 * @property {float} average_rating
 * @property {string} profile_image
 * @property {ScheduleDay[]} schedule
 */

/**
 * @typedef ScheduleDay
 * @property {Object} mon
 * @property {string} mon.start_time - Start time in HHMM format.
 * @property {string} mon.end_time - End time in HHMM format.
 * @property {string} mon.durations - Duration of each slot in minutes.
 * @property {string} mon.intervals - Interval between slots in minutes.
 * @property {string} mon.spaces - Number of spaces available per slot.
 * @property {Object} mon.slots - Slots available for the day.
 * @property {Object} mon.slots["HHMM-HHMM"] - Specific time slot.
 * @property {string} mon.slots["HHMM-HHMM"].spaces - Spaces available for the specific slot.
 * @property {Object} tue - Same structure as `mon` for Tuesday.
 * @property {string} tue.start_time
 * @property {string} tue.end_time
 * @property {string} tue.durations
 * @property {string} tue.intervals
 * @property {string} tue.spaces
 * @property {Object} tue.slots
 * @property {Object} tue.slots["HHMM-HHMM"]
 * @property {string} tue.slots["HHMM-HHMM"].spaces
 */

/**
 * Helper function to handle fetch requests
 * @param {string} url
 * @param {object} options
 * @returns {Promise<any>}
 */
async function fetchData(url, options = {}) {

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeader(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(JSON.stringify(await response.json()));
  }

  return response.json();
}

/**
 * Returns list of medical staff
 */
export async function getCitaMedicaDoctors() {
  return fetchData(`${BASE_URL}insurance/cita-medica-doctors/`);
}

/**
 * Returns list of specialties
 */
export async function getCitaMedicaSpecialties() {
  return fetchData(`${BASE_URL}insurance/cita-medica-specialties/`);
}

/**
 * Update list of medical staff
 */
export async function updateCitaMedicaDoctors(data) {
  try {
    const response = await fetch(
      `${BASE_URL}insurance/cita-medica-doctors-update/`,
      {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
        },
      }
    );

    // Retrieve the response success data field
    const responseData = await response.json();
    const success = responseData.success;

    // Check if the response is successful
    if (!success) {
      throw new Error(JSON.stringify(responseData));
    }

    return {
      type: "success",
      message: "Actualización exitosa",
    };

  } catch (error) {
    console.error("Error updating medical staff: ", error);
    return {
      type: "error", message:
        "Error actualizando personal médico"
    };
  }
}

/**
 * Fetch doctors grouped by their category or specialty
 * @param {Array<number>} specialties - List of specialty IDs to filter doctors
 */
export async function getConsultDoctors(specialties) {

  try {
    // Convert specialties array to a comma-separated string
    const queryParams = new URLSearchParams({
      specialities: specialties.join(","),
    });

    const response = await fetch(
      `${BASE_URL}insurance/cita-medica-doctors-specialties/?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
        },
      }
    );

    const responseData = await response.json();

    // Transform the response data to match the desired structure
    const transformedData = responseData.map((doctor, index) => ({
      // id: index + 1, // Generate a new sequential ID
      id: parseInt(doctor.id, 10), // Ensure ID is an integer
      name: doctor.name,
      averageRating: parseFloat(doctor.average_rating) || 0, // Convert rating to float or default to 0
      specialities: doctor.specialities.map((specialty) => ({
        id: parseInt(specialty.id, 10), // Ensure ID is an integer
        name: specialty.name,
      })),
      profileImage: doctor.url_profile_image,
      is_assigned: doctor.is_assigned === "yes", // Convert "yes"/"no" to boolean
      schedule: doctor.schedule,
      phoneNumber: doctor.phone,
    }));

    return {
      type: "success",
      message: "Doctores obtenidos exitosamente",
      data: transformedData,
    };
  } catch (error) {
    console.error("Error fetching doctors: ", error);
    return {
      type: "error",
      message: "Error obteniendo doctores",
    };
  }
}

/**
 * Sends consultation requirements to the backend to generate bookings.
 * @param {number} medicalCaseId - The ID of the medical case.
 * @param {Array} consultationRequirements - List of consultation requirements.
 * @returns {Promise} - Axios response promise.
 */
export const updateBookings = async ({medicalCaseId, patientName, patientPhone, patientDni, consultationRequirements}) => {
  const payload = {
    medicalCaseId,
    patientName,
    patientPhone,
    patientDni,
    consultationRequirements,
  };

  try {

    const response = await fetch(`${BASE_URL}insurance/cita-medica-generate-booking/`, {
      method: "POST",
      headers: {
        ...getAuthHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(JSON.stringify(responseData));
    }

    return {
      type: "success",
      message: "Reserva generada exitosamente",
    };

  } catch (error) {
    console.error("Error generando la reserva: ", error);
    return {
      type: "error",
      message: "Error generando la reserva",
    };
  }
}

/**
 * Fetches bookings for a medical case.
 * @param {number} medicalCaseId - The ID of the medical case.
 * @returns {Promise} - Processed response with bookings and doctor options.
 */
export const getCitaMedicaBookings = async (medicalCaseId) => {
  try {
    const response = await fetch(
      `${BASE_URL}insurance/cita-medica-case-details/?medicalCaseId=${medicalCaseId}`,
      {
        method: "GET",
        headers: {
          ...getAuthHeader(),
          "Content-Type": "application/json",
        },
      }
    );

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(JSON.stringify(responseData));
    }

    // Process the response data
    const processedData = responseData.data.map((requirement) => ({
      requirementId: requirement.requirementId,
      requirementName: requirement.requirementName,
      booking: requirement.booking
        ? {
            bookingId: requirement.booking.bookingId,
            doctorId: requirement.booking.doctorId,
            doctorName: requirement.booking.doctorName,
            date: requirement.booking.date,
            time: requirement.booking.time,
            amount: requirement.booking.amount,
            status: requirement.booking.status,
          }
        : null,
      doctorOptions: requirement.doctorOptions
        ? requirement.doctorOptions.map((doctor) => ({
            id: doctor.id,
            name: doctor.name,
            specialities: doctor.specialities.map((specialty) => ({
              id: specialty.id,
              name: specialty.name,
              requirementId: specialty.requirement_id,
            })),
            isAssigned: doctor.is_assigned === "yes",
            averageRating: parseFloat(doctor.average_rating) || 0,
            profileImage: doctor.url_profile_image,
            schedule: doctor.schedule,
            phone: doctor.phone,
          }))
        : [],
    }));

    return {
      type: "success",
      message: "Reservas obtenidas exitosamente",
      data: processedData,
    };
  } catch (error) {
    console.error("Error obteniendo reservas: ", error);
    return {
      type: "error",
      message: "Error obteniendo reservas",
    };
  }
};